import {defaultMarkdownSerializer, type MarkdownSerializer, MarkdownSerializerState} from 'prosemirror-markdown';
import {type Mark, type Schema, Slice, Fragment, type Node} from 'prosemirror-model';

// Эта функция из prosemirror-markdown
// Используется в дефолтной реализации сериалайзера для marks.link
function isPlainURL(link: Mark, parent: Node, index: number) {
    if (link.attrs.title || !(/^\w+:/).test(link.attrs.href)) {
        return false;
    }
    const content = parent.child(index);
    if (!content.isText || content.text !== link.attrs.href || content.marks[content.marks.length - 1] !== link) {
        return false;
    }
    return index === parent.childCount - 1 || !link.isInSet(parent.child(index + 1).marks);
}

const nodesToClose = ['blockquote', 'orderedList', 'bulletList'];
class TimeMarkdownSerializerState extends MarkdownSerializerState {
    closed: Node | null = null;

    // По дефолту prosemirror-markdown использует двойной перенос \n\n для закрытия блока форматирования,
    // что является избыточным для всех нод, кроме цитат и списков.
    closeBlock(node: Node): void {
        if (nodesToClose.includes(node.type.name)) {
            this.closed = node;
        } else {
            this.write('\n');
        }
    }

    // Изменил регулярные выражения для поддержки кириллицы в случае
    // с экранированием спецсимволов /[`*\\~[\]_]/g (пример - тэги на кириллице с _ внутри текста).
    // В остальном - дефолтная реализация esc из MarkdownSerializerState.
    esc(str: string, startOfLine = false) {
        // eslint-disable-next-line no-param-reassign
        str = str.replace(
            /[`*\\~[\]_]/g,
            (m, i) => (m === '_' && i > 0 && i + 1 < str.length && str[i - 1]!.match(/\w|[а-яА-Я]/) && str[i + 1]!.match(/\w|[а-яА-Я]/) ? m : '\\' + m),
        );
        if (startOfLine) {
            // eslint-disable-next-line no-param-reassign
            str = str.replace(/^(\+[ ]|[-*>])/, '\\$&').replace(/^(\s*)(#{1,6})(\s|$)/, '$1\\$2$3').replace(/^(\s*\d+)\.\s/, '$1\\. ');
        }
        if (this.options.escapeExtraCharacters) {
            // eslint-disable-next-line no-param-reassign
            str = str.replace(this.options.escapeExtraCharacters, '\\$&');
        }
        return str;
    }
}

export function getMarkdownSerializer(schema: Schema) {
    const serializer: MarkdownSerializer = defaultMarkdownSerializer;
    serializer.marks.bold = {open: '**', close: '**', mixable: true, expelEnclosingWhitespace: true};
    serializer.marks.italic = {open: '*', close: '*', mixable: true, expelEnclosingWhitespace: true};
    serializer.marks.strike = {open: '~~', close: '~~', mixable: true, expelEnclosingWhitespace: true};
    serializer.marks.link = {
        open(state, mark, parent, index) {
            const isPlain = isPlainURL(mark, parent, index);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            state.inAutolink = isPlain;

            // В дефолтном сериалайзере мы возвращаем '<' вместо пустой строки
            return isPlain ? '' : '[';
        },
        close(state, mark) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const {inAutolink} = state;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            state.inAutolink = undefined;

            // В дефолтном сериалайзере мы возвращаем '>' вместо пустой строки
            return inAutolink ? '' : '](' + mark.attrs.href.replace(/[\\(\\)"]/g, '\\$&') + (mark.attrs.title ? ` "${mark.attrs.title.replace(/"/g, '\\"')}"` : '') + ')';
        },
        mixable: true,
    };
    serializer.marks.mention = {open: '', close: '', mixable: true};

    Object.entries(schema.nodes).forEach(([name, tiptapNode]) => {
        if (!tiptapNode.spec.toText) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        serializer.nodes[name] = (state: MarkdownSerializerState, node: Node, parent?: Node, index?: number) => {
            state.write(tiptapNode.spec.toText({node, pos: -1, parent, index}));
            if (!tiptapNode.spec.inline) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                state.closeBlock(node);
            }
        };
    });

    serializer.nodes.bulletList = serializer.nodes.bullet_list;
    serializer.nodes.codeBlock = serializer.nodes.code_block;
    serializer.nodes.hardBreak = serializer.nodes.hard_break;
    serializer.nodes.codeBlock = (state, node) => {
        // Make sure the front matter fences are longer than any dash sequence within it
        const backticks = node.textContent.match(/`{3,}/gm);
        const fence = backticks ? (backticks.sort().slice(-1)[0] + '`') : '```';

        state.write(fence + (node.attrs.language || '') + '\n');
        state.text(node.textContent, false);
        state.ensureNewLine();
        state.write(fence);
        state.closeBlock(node);
    };
    serializer.nodes.emoji = (state, node) => {
        state.text(`:${node.text!}:`, !state.inAutolink);
    };

    //serializer.nodes['heading']
    serializer.nodes.horizontalRule = (state, node) => {
        state.write('***');
        state.closeBlock(node);
    };

    //serializer.nodes['image']
    serializer.nodes.listItem = serializer.nodes.list_item;

    serializer.nodes.orderedList = (state, node) => {
        const start = node.attrs.start || 1;
        const maxW = String(start + (node.childCount - 1)).length;
        const space = state.repeat(' ', maxW + 2);
        state.renderList(node, space, (i) => {
            const nStr = String(start + i);
            return state.repeat(' ', maxW - nStr.length) + nStr + '. ';
        });
    };

    serializer.nodes.paragraph = (state, node, parent) => {
        state.renderInline(node);

        if (parent.isBlock) {
            state.closeBlock(node);
        }
    };

    serializer.nodes.table = (state, node, parent) => {
        state.renderContent(node);

        if (parent.isBlock) {
            state.closeBlock(node);
        }
    };

    serializer.nodes.tableRow = (state, node, parent, index) => {
        const isHeader = index === 0;
        node.children.forEach((node) => {
            state.write('|');
            node.children.forEach((node) => {
                state.renderInline(node, false);
            });
        });
        state.write('|\n');

        if (isHeader) {
            node.children.forEach(() => {
                state.write('| -------- ');
            });
            state.write('|\n');
        }
    };

    // TIME-13515
    // пробел (\u200b) нужен для того, чтобы marked корректно парсил зачёркнутные упоминания
    // в marked "~~~" это начало код блока, теперь же маркед корректно распознаёт упоминание канала (~~\u200b~channel~~)
    serializer.nodes.timeEditorChannelMentionExtension = (state, node, parent, index) => {
        const channelMentionNode = schema.nodes.timeEditorChannelMentionExtension;

        if (!channelMentionNode) {
            return;
        }

        const isStrikeActive = node.marks.some((mark) => mark.type.name === 'strike');
        const channelMentionText = channelMentionNode.spec.toText({node, pos: -1, parent, index});
        state.write(`${isStrikeActive ? '\u200b' : ''}${channelMentionText}`);
    };

    // Метод сериализации класса MarkdownSerializer.
    // Переопределяем, чтобы использовать класс TimeMarkdownSerializerState вместо дефолтного MarkdownSerializerState
    serializer.serialize = (content: Node, options: {tightLists?: boolean} = {}) => {
        Object.assign(serializer.options, options);
        const state = new TimeMarkdownSerializerState(serializer.nodes, serializer.marks, serializer.options);
        state.renderContent(content);
        return state.out.trimEnd();
    };

    //serializer.nodes['text']
    return serializer;
}

const THIN_SPACE = ' ';

export function formatLimitCounter(value: number | string) {
    return value.toString().replace(/(\S)(?=(\S{3})+(?!\S))/g, `$1${THIN_SPACE}`);
}

export function cleanMarkdown(text: string): string {
    return text.replace(/\\\n{1}/gm, '\n');
}

const MAX_DEPTH = 10;

export const mapFragment = (
    fragment: Fragment,
    callback: (node: Node, index: number) => Node,
    depth = 0,
): Fragment =>
    (depth > MAX_DEPTH ? fragment : Fragment.fromArray(
        fragment.content.map((child, index) => {
            const node = callback(child, index);
            if (node.eq(child)) {
                return child.copy(mapFragment(child.content, callback, depth + 1));
            }
            return node;
        }),
    ));

export const mapSlice = (
    slice: Slice,
    callback: (node: Node, index: number) => Node,
): Slice => {
    const fragment = mapFragment(slice.content, callback);
    return new Slice(fragment, slice.openStart, slice.openEnd);
};

export function removeNonSelectableElements(rawHtml: string): string {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(rawHtml, 'text/html');

    const nonSelectableElements = parsedDocument.querySelectorAll(
        '[style*="user-select: none"], ' +
        '[style*="-webkit-user-select: none"], ' +
        '[style*="pointer-events: none"]',
    );

    nonSelectableElements.forEach((el) => {
        if (el.parentElement) {
            el.remove();
        }
    });

    return parsedDocument.body.innerHTML;
}

export function findAncestorAnchor(node: Partial<HTMLElement> | null) {
    if (node?.closest) {
        return node.closest('a');
    }

    return null;
}
